import React from 'react';
import { useGetContentPlanQuery, ContentPlanItem } from 'store/services/admin/adminSeoApi';
import './ContentPlanTab.css';

const getPositionClass = (position: number): string => {
  if (position <= 10) return 'position-green';
  if (position <= 15) return 'position-yellow';
  return 'position-orange';
};

interface Props {
  isActive: boolean;
}

const ContentPlanTab: React.FC<Props> = ({ isActive }) => {
  const { data, isLoading, error } = useGetContentPlanQuery(undefined, { skip: !isActive });

  if (isLoading) {
    return <div className="seo-loading">Loading content plan...</div>;
  }

  if (error) {
    return (
      <div className="seo-error-state">
        <h3>Failed to load content plan</h3>
        <p>Please try again later.</p>
      </div>
    );
  }

  const isPlan = data && !('status' in data);

  if (!isPlan) {
    return (
      <div className="seo-empty-state">
        <h3>No content plan has been generated yet.</h3>
        <p>Click &quot;Full Analysis&quot; to generate a prioritized content plan.</p>
      </div>
    );
  }

  return (
    <>
      {/* Summary Cards */}
      <div className="seo-stats seo-stats-3">
        <div className="seo-stat-card">
          <span className="seo-stat-label">P1 Articles</span>
          <span className="seo-stat-value">{data.summary.p1Count}</span>
        </div>
        <div className="seo-stat-card">
          <span className="seo-stat-label">P2 Articles</span>
          <span className="seo-stat-value cp-stat-gray">{data.summary.p2Count}</span>
        </div>
        <div className="seo-stat-card">
          <span className="seo-stat-label">Total Est. Impressions</span>
          <span className="seo-stat-value">{data.summary.totalEstimatedImpressions.toLocaleString()}</span>
        </div>
      </div>

      {/* Content Cards */}
      <div className="cp-card-list">
        {data.items.map((item: ContentPlanItem, idx: number) => (
          <div className="cp-card" key={idx}>
            <div className="cp-card-header">
              <span className={`cp-priority-badge ${item.priority === 'P1' ? 'cp-p1' : 'cp-p2'}`}>
                {item.priority}
              </span>
              <h3 className="cp-card-title">{item.title}</h3>
            </div>

            <div className="cp-card-meta">
              <span className="cp-meta-keyword">{item.targetKeyword}</span>
              <span className={`seo-position-badge ${getPositionClass(item.currentPosition)}`}>
                {item.currentPosition.toFixed(1)}
              </span>
              <span className="cp-meta-impressions">{item.estimatedImpressions.toLocaleString()} imp</span>
              <span className="cp-intent-badge">{item.searchIntent}</span>
            </div>

            <p className="cp-card-brief">{item.brief}</p>

            {item.competitors.length > 0 && (
              <div className="cp-card-competitors">
                Competing with: {item.competitors.join(', ')}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default ContentPlanTab;
